<template>
  <div class="records-view">
    <van-nav-bar
        title="Assets Records"
        left-arrow
        fixed
        placeholder
        @click-left="onClickLeft"
        class="custom-navbar"
    />

    <div class="content-area">
      <van-list
          v-model:loading="loading"
          :finished="finished"
          loading-text="Loading..."
          finished-text="No more records"
          @load="onLoad"
          class="records-list"
      >
        <div v-for="(record, index) in records" :key="index" class="record-card">
          <div class="record-main">
            <div class="record-icon" :class="getRecordTypeClass(record.logType)">
              <span class="dollar-icon">$</span>
            </div>
            <div class="record-info">
              <div class="record-type">{{ changeTypes[record.logType] || record.logType }}</div>
              <div class="record-date">{{ formatDate(record.createTime) }}</div>
            </div>
            <div class="record-amount" :class="getAmountClass(record.balanceChange)">
              <span v-if="record.balanceChange > 0" class="amount-sign">+</span>
              <span v-else class="amount-sign">-</span>
              {{ Math.abs(record.balanceChange) }} {{ getCurrency() }}
            </div>
          </div>
        </div>
      </van-list>
    </div>
  </div>
</template>

<script setup>
import { ref } from 'vue'
import { getCurrency, getWithdrawRecords } from '@/utils/api'

const onClickLeft = () => history.back()
const records = ref([])
const loading = ref(false)
const finished = ref(false)
const pageNum = ref(1)
const pageSize = ref(10)

const changeTypes = ref({
  pay_in: 'Pay Order',
  pay_dj: 'Correct Frozen',
  pay_djht: 'Correct Payback',
  invitor_dj: 'Correct Invite Frozen',
  invitor_return: 'Commission',
  invitor_djht: 'Correct Invite Payback',
  sys_in: 'Platform Reward',
  sys_out: 'Platform Deduction',
  withdraw_out: 'Withdraw Apply',
  withdraw_out_success: 'Withdraw Success',
  withdraw_out_fail: 'Withdraw Refund',
  pay_correct: 'Pay Correct',
  dividend_in: 'Pay Reward',
})

const getRecordTypeClass = (logType) => {
  if (logType.includes('in') || logType.includes('return') || logType.includes('success')) {
    return 'type-income'
  }
  if (logType.includes('out') || logType.includes('fail')) {
    return 'type-expense'
  }
  return 'type-neutral'
}


const getAmountClass = (amount) => {
  return amount > 0 ? 'amount-positive' : 'amount-negative'
}

const formatDate = (dateString) => {
  if (!dateString) return ''
  const date = new Date(dateString)
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  const hours = String(date.getHours()).padStart(2, '0')
  const minutes = String(date.getMinutes()).padStart(2, '0')
  return `${month}-${day} ${hours}:${minutes}`
}

const fetchRecords = async () => {
  try {
    loading.value = true
    const res = await getWithdrawRecords({
      pageNum: pageNum.value,
      pageSize: pageSize.value,
      orderByColumn: 'createTime',
      isAsc: 'DESC',
    })

    if (res.rows && res.rows.length > 0) {
      // 检测 MyBatis reasonable=true 的情况：如果返回的数据第一条已存在于列表中，说明已经到底了
      const firstNewItemId = res.rows[0]?.id
      const isDuplicate = records.value.length > 0 && records.value.some(item => item.id === firstNewItemId)
      
      if (isDuplicate) {
        // 返回的数据与已有数据重复，说明已经到底了（reasonable=true导致返回了第一页）
        finished.value = true
      } else {
        // 正常情况：添加新数据
        records.value = [...records.value, ...res.rows]
        pageNum.value += 1
        // 如果返回的数据少于pageSize，说明已经到底了
        if (res.rows.length < pageSize.value) {
          finished.value = true
        }
      }
    } else {
      finished.value = true
    }
  } catch (error) {
    console.error('Error fetching records:', error)
  } finally {
    loading.value = false
  }
}

const onLoad = () => {
  fetchRecords()
}
</script>

<style scoped>
.records-view {
  background: #f8f9fa;
  min-height: 100vh;
}

:deep(.custom-navbar) {
  background: #fff;
}

.content-area {
  padding: 12px;
}

.records-list {
  padding-bottom: 16px;
}

.record-card {
  background: #fff;
  border-radius: 16px;
  padding: 10px 12px;
  margin-bottom: 10px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.03);
}

.record-main {
  display: flex;
  align-items: center;
  gap: 8px;
}

.record-icon {
  width: 36px;
  height: 36px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  flex-shrink: 0;
}

.dollar-icon {
  font-weight: 800;
  font-size: 18px;
}

.record-icon.type-income {
  background: rgba(76, 217, 100, 0.15);
  color: #4cd964;
}

.record-icon.type-expense {
  background: rgba(255, 59, 48, 0.15);
  color: #ff3b30;
}

.record-icon.type-neutral {
  background: rgba(142, 142, 147, 0.15);
  color: #8e8e93;
}

.record-info {
  flex: 1;
  min-width: 0;
  padding: 0 2px;
}

.record-type {
  font-size: 14px;
  font-weight: 700;
  color: #1a1a1a;
  margin-bottom: 3px;
  line-height: 1.3;
}

.record-date {
  font-size: 11px;
  color: #8e8e93;
  line-height: 1.3;
}

.record-amount {
  font-size: 15px;
  font-weight: 800;
  flex-shrink: 0;
  text-align: right;
  white-space: nowrap;
  padding-left: 4px;
}

.record-amount.amount-positive {
  color: #4cd964;
}

.record-amount.amount-negative {
  color: #ff3b30;
}

.amount-sign {
  font-size: 14px;
  margin-right: 2px;
}
</style>
